import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Box,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Icon,
  Image,
  Flex,
} from "@chakra-ui/react";
import { FaPrint } from "react-icons/fa";
import { useCognito } from "../utils/useCognito";
import { formatDateTime } from "../utils/dateUtils";
import { calculateInvoiceSummary } from "../utils/calculateInvoiceSummary";

const INVOICE_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getInvoice";

const ViewInvoiceModal = ({ isOpen, onClose, invoiceId, facilityName }) => {
  const { getIdToken } = useCognito();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const printableContentRef = useRef();

  useEffect(() => {
    if (!isOpen || !invoiceId) return;

    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const token = getIdToken();

        const res = await fetch(INVOICE_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ invoiceId }),
        });

        const data = await res.json();
        setInvoice(data.invoices?.[0] || null);
      } catch (err) {
        console.error("Failed to load invoice:", err);
        setInvoice(null);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [isOpen, invoiceId, getIdToken]);

  const handlePrint = () => {
    if (!printableContentRef.current) return;
    const content = printableContentRef.current.innerHTML;
    const printWindow = window.open("", "", "width=800,height=600");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${invoice?.invoiceId}</title>
          <link href="https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600&display=swap" rel="stylesheet">
          <style>
            body {
              font-family: 'Rajdhani', sans-serif;
              margin: 24px;
              color: #1e1e1e;
              line-height: 1.3;
              font-size: 13px;
            }
            .invoice-header {
              display: flex;
              justify-content: center;
              align-items: center;
              margin-bottom: 16px;
            }
            .logo-container img {
              height: 38px;
              width: auto;
            }
            h3 {
              text-align: center;
              font-size: 18px;
              margin-bottom: 12px;
              color: #207d87;
            }
            .invoice-details {
              margin-bottom: 12px;
            }
            .invoice-details div {
              margin-bottom: 4px;
            }
            .invoice-details strong {
              color: #3d3d3d;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 12px;
              font-size: 12px;
            }
            th, td {
              border: 1px solid #d0d0d0;
              padding: 6px 8px;
            }
            th {
              background-color: #f5f5f5;
              color: #3d3d3d;
              text-align: left;
            }
            .badge-paid {
              background-color: #207d87;
              color: white;
              padding: 2px 6px;
              border-radius: 4px;
            }
            .badge-unpaid {
              background-color: #c93c3c;
              color: white;
              padding: 2px 6px;
              border-radius: 4px;
            }
            .totals-section {
              margin-top: 12px;
              text-align: right;
              font-weight: bold;
              font-size: 13px;
            }
            .footer {
              text-align: center;
              margin-top: 28px;
              font-size: 12px;
              color: #555;
            }
            .footer img {
              margin-top: 6px;
              height: 60px;
            }
            a {
              color: #1e6e7d;
              word-break: break-word;
            }
          </style>
        </head>
        <body>
          ${content}
          <div class="totals-section">
            ${invoice?.paid
              ? `
              <div>Total Paid: $${Number(invoice.totalDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div>Total Due: $0.00</div>
              `
              : `
              <div>Total Due: $${Number(invoice.totalDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              `
            }
          </div>
          <div class="footer">
            <p>Thank you for your business!</p>
            <img src="https://www.curagenesis.com/assets/img/dnahelix.webp" alt="CuraGenesis DNA Logo" />
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };


  const formatAddress = (address) => {
    if (!address) return "—";
    const { street, city, state, zip } = address;
    const parts = [street, city, state, zip].filter(Boolean);
    return parts.join(", ");
  };

  const { subtotal, discount } = calculateInvoiceSummary(invoice);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Invoice Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Spinner />
          ) : invoice ? (
            <Box ref={printableContentRef} px={4} py={2}>
              <div className="invoice-header">
                <div className="logo-container">
                  <img
                    src="https://www.curagenesis.com/assets/img/cglogotext.webp"
                    alt="CuraGenesis Logo"
                    style={{ height: "40px", width: "auto" }}
                  />
                </div>
              </div>

              <div className="invoice-summary">
                <h3>Invoice Summary</h3>
              </div>

              <Box mb={4}>
                <Text><strong>Invoice ID:</strong> {invoice.invoiceId}</Text>
                {invoice.physicianName && (
                  <Text><strong>Physician:</strong> {invoice.physicianName}</Text>
                )}
                <Text><strong>Facility Name:</strong> {facilityName || "—"}</Text>
                {invoice.facilityAddress && (
                  <Text><strong>Facility Address:</strong> {formatAddress(invoice.facilityAddress)}</Text>
                )}
                <Text><strong>Order Date:</strong> {formatDateTime(invoice.orderCreatedDate)}</Text>
                <Text><strong>Due Date:</strong> {formatDateTime(invoice.dueDate)}</Text>
                <Text>
                  <strong>Paid:</strong>{" "}
                  <span className={invoice.paid ? "badge-paid" : "badge-unpaid"}>
                    {invoice.paid ? "Yes" : "No"}
                  </span>
                </Text>
              </Box>

              <Box mt={4}>
                {/* <Text fontSize="lg" fontWeight="bold" mb={2}>
                  Line Items
                </Text> */}
                <Table variant="simple" size="sm" width="100%">
                  <Thead>
                    <Tr>
                      <Th>Label</Th>
                      <Th isNumeric>Qty</Th>
                      <Th isNumeric>Unit Price</Th>
                      <Th isNumeric>Total</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {invoice.lineItems?.map((item, idx) => {
                      const isDiscount = /prompt payment discount/i.test(item.label);
                      const displayQty = isDiscount ? `-${invoice.discountPercentage}%` : item.quantity;

                      return (
                        <Tr key={idx}>
                          <Td>{item.label}</Td>
                          <Td isNumeric>{displayQty}</Td>
                          <Td isNumeric>
                            ${Number(item.unitPrice).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </Td>
                          <Td isNumeric>
                            ${Number(item.total).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              <Box mt={6} borderTop="1px solid #e2e8f0" pt={4}>
                <Box display="flex" justifyContent="flex-end" flexDirection="column" alignItems="flex-end">
                  <Text><strong>Subtotal:</strong> ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                  {discount > 0 && (
                    <Text color="green.600">
                      <strong>Discount:</strong> -${discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Text>
                  )}
                  <Text fontSize="xl" fontWeight="bold" mt={2}>
                    {invoice.paid ? "Total Paid" : "Total Due"}: $
                    {Number(invoice.totalDue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </Text>
                  {!invoice.paid && (
                    <Text mt={6} fontSize="sm" color="gray.700">
                      To access wiring instructions, go to{" "}
                      <a
                        href="https://www.curagenesis.com/assets/pdf/CuraGenesis_Wire_Transfer_Instructions.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://www.curagenesis.com/assets/pdf/CuraGenesis_Wire_Transfer_Instructions.pdf
                      </a>
                    </Text>
                  )}
                </Box>
              </Box>
              
            </Box>
          ) : (
            <Text>Invoice not found or failed to load.</Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            leftIcon={<Icon as={FaPrint} />}
            colorScheme="blue"
            onClick={handlePrint}
            isDisabled={loading || !invoice}
            mr={3}
          >
            Print Invoice
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ViewInvoiceModal;
